import { useCallback, useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  FormErrorMessage,
  VStack,
  Divider,
  Flex,
  Heading,
  Icon,
  useToast,
  Box,
  Text,
  Circle,
  HStack,
} from "@chakra-ui/react";
import {
  AccountTypeLabels,
  Proposal,
  ProposalCustomStatus,
} from "@/models/proposal.model";
import { Field, FieldProps, Form, Formik, FormikHelpers } from "formik";
import {
  MdAccountBalance,
  MdPerson,
  MdNavigateNext,
  MdNavigateBefore,
} from "react-icons/md";
import { PaymentMethodTypes } from "@/models/investor.model";
import { mapPaymentMethodToPixKeyType, mapPixKeyTypeToLabel } from "./payMethodMapper";
import { getBanks } from "@/services/bank.service";
import { bankDetailModalValidationSchema } from "./BankDetailModalValidation";
import InputMask from "react-input-mask";
import { emailChecker } from "@/utils/EmailChecker";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { getAddressViaCep } from "@/services/viaCep.service";
import { cepFormatter } from "@/utils/cepFormatter";
import { statesArray } from "@/utils/statesArray";
import { MdSearch, MdHome } from "react-icons/md";
import {
  addressValidationSchema,
  personalDataValidationSchema,
} from "../ProposalForm/validations";
import { validateAndFormatPhone } from "@/utils/phoneChange" ;
import { retryProposalUtil } from "@/utils/retryProposal";
import { getProposalById } from "@/services/proposal.service";
import { useParams } from "react-router-dom";
import { formatErrorMessage } from "@/utils/formatErrorMessage";


interface BankDetailUpdateFormProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId?: string;
}

// Componente do Step Indicator
const StepIndicator = ({ currentStep }: { currentStep: number }) => {
  const steps = [
    { number: 1, title: "Dados Pessoais", icon: MdPerson },
    { number: 2, title: "Endereço", icon: MdHome },
    { number: 3, title: "Dados Bancários", icon: MdAccountBalance },
  ];

  return (
    <HStack spacing={4} justify="center" mb={6}>
      {steps.map((step, index) => (
        <Flex key={step.number} align="center">
          <Flex direction="column" align="center">
            <Circle
              size="40px"
              bg={currentStep >= step.number ? "green.500" : "gray.200"}
              color={currentStep >= step.number ? "white" : "gray.500"}
              fontWeight="bold"
              mb={2}
            >
              <Icon as={step.icon} boxSize={5} />
            </Circle>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color={currentStep >= step.number ? "green.500" : "gray.500"}
              textAlign="center"
            >
              {step.title}
            </Text>
          </Flex>
          {index < steps.length - 1 && (
            <Box
              w="40px"
              h="2px"
              bg={currentStep > step.number ? "green.500" : "gray.200"}
              mx={2}
              mt="-20px"
            />
          )}
        </Flex>
      ))}
    </HStack>
  );
};

export default function BankAndPersonalDetailModal({
  isOpen,
  onClose,
  proposalId,
}: BankDetailUpdateFormProps) {
  const [banks, setBanks] = useState<Array<{ id: string; name: string; ispb: string }>>(
    []
  );
  const { id:paramId } = useParams<{ id: string }>();
  const finalId = proposalId ?? paramId ?? null;
  const [isLoading, setIsLoading] = useState(false);
  const [isPixKeyValid, setIsPixKeyValid] = useState(true);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [birthDateProp, setBirthDateProp] = useState<string>("");
  const [isInputReadOnly, setIsInputReadOnly] = useState<boolean>(false);
  const [ proposal, setProposal ] = useState<Proposal | null>();
  const [customStatus, setCustomStatus] = useState<any>({});

  useEffect(() => {
    if (!finalId) return;

    const fetchProposal = async () => {
      try {
        setIsLoading(true);
        const data = await getProposalById(finalId);
        setProposal(data);
      } catch (error) {
        console.error("Erro ao buscar proposta:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchProposal();
    }
  }, [isOpen]);

  const isZeroTac = proposal?.chosenFinancialInfo?.Product?.name?.toUpperCase() === "ZERO TAC";
  const paymentMethods = isZeroTac
    ? [PaymentMethodTypes.BANK_ACCOUNT]
    : proposal?.chosenFinancialInfo?.Product?.processor?.investors?.[0]?.paymentMethods || [
        PaymentMethodTypes.BANK_ACCOUNT,
        PaymentMethodTypes.DOCUMENT,
        PaymentMethodTypes.EMAIL,
        PaymentMethodTypes.PHONE,
      ];

  // const isOnlyBankAccount =
  //   paymentMethods.length === 1 && paymentMethods[0] === PaymentMethodTypes.BANK_ACCOUNT;

  // const overriddenValues = isOnlyBankAccount
  //   ? { pixKeyType: "BANK_ACCOUNT", pixKey: "" }
  //   : {};

  // const initialValues: BankDetail = {
  //   ...(proposal.BankDetail || {}),
  //   ...overriddenValues,
  // };

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await getBanks(1, 22);
        setBanks(
          response.data.map((bank: any) => ({
            id: bank.id,
            name: bank.name,
            ispb: bank.ispb,
          }))
        );
      } finally {
        setIsLoading(false);
      }
    };
    if (isOpen) {
      fetchBanks();
    }
  }, [isOpen]);

  // Reset step quando modal abre
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!proposal?.customStatus) return;

    let parsedStatus = {};

    if (typeof proposal.customStatus === "string") {
      try {
        const temp = JSON.parse(proposal.customStatus);
        parsedStatus = { ...parsedStatus, ...temp };
      } catch (e) {
        console.warn("customStatus não é JSON válido:", proposal.customStatus);
      }
    } else if (
      typeof proposal.customStatus === "object" &&
      proposal.customStatus !== null
    ) {
      parsedStatus = {
        ...parsedStatus,
        ...(proposal.customStatus as ProposalCustomStatus),
      };
    }

    setCustomStatus(parsedStatus);
  }, [proposal]);

  const handleProposalRetry = async (values: any, actions: FormikHelpers<any>) => {
    await retryProposalUtil({
      values,
      actions,
      proposal: proposal!,
      createProposalBank: true,
      onSuccess: () => {
        toast({
          title: "Dados Complementares Enviados",
          description: "Estamos revalidando sua proposta.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();
      },
      onError: () => {
        toast({
          title: "Erro",
          description:
            "Houve um erro ao atualizar seus dados complementares. Por favor, tente novamente em instantes.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    });
  };

  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBirthDateProp(e.target.value);
  };


  const formatDateToBR = (dateString: string): string => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) return "";

      // Remover o +1 no dia que está causando o erro
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error("Erro ao formatar data:", error);
      return "";
    }
  };

  const normalizeInput = (input: string) =>
    input
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const handleCepSearch = async (cep: string, setFieldValue: any) => {
    const cleanCep = cep.replace("-", "");
    if (cleanCep.length === 8) {
      setIsCepLoading(true);
      try {
        const data = await getAddressViaCep(cleanCep);
        if (!data.erro) {
          setFieldValue("street", data.logradouro.toUpperCase());
          setFieldValue("district", data.bairro.toUpperCase());
          setFieldValue("city", data.localidade.toUpperCase());
          setFieldValue("state", data.uf);
          setIsInputReadOnly(true);
        } else {
          toast({
            title: "CEP não encontrado",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setIsInputReadOnly(false);
        }
      } catch (error) {
        toast({
          title: "Erro ao buscar CEP",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setIsInputReadOnly(false);
      } finally {
        setIsCepLoading(false);
      }
    }
  };

  // const handleAddressSubmit = async (values: any, actions: FormikHelpers<any>) => {
  //   try {
  //     const updatedProposal = {
  //       id: proposal?.NaturalPerson?.id,
  //       address: {
  //         id: proposal?.NaturalPerson?.addressId!,
  //         street: values.street,
  //         number: values.streetNumber,
  //         complement: values.complement,
  //         district: values.district,
  //         city: values.city,
  //         state: values.state,
  //         cep: values.cep.replace("-", ""),
  //       },
  //     };
  //     // await updateNaturalPerson(updatedProposal);
  //     setAddress(updatedProposal.address);

  //     // toast({
  //     //   title: "Endereço Atualizado",
  //     //   description: "O endereço foi atualizado com sucesso",
  //     //   status: "success",
  //     //   duration: 5000,
  //     //   isClosable: true,
  //     // });

  //     // Avançar para o próximo step após sucesso
  //     setCurrentStep(3);
  //   } catch {
  //     toast({
  //       title: "Erro",
  //       description: "Houve um erro ao atualizar o endereço.",
  //       status: "error",
  //       duration: 5000,
  //       isClosable: true,
  //     });
  //   } finally {
  //     actions.setSubmitting(false);
  //   }
  // };

  function formatarTelefone(telefone: string): string {
    // Remover tudo que não for dígito
    const digitsOnly = telefone.replace(/\D/g, "");

    // Se não tiver o número mínimo de dígitos, retornar vazio
    if (digitsOnly.length < 10) {
      return "";
    }

    // Formatar como (XX) XXXXX-XXXX
    return `(${digitsOnly.substring(0, 2)}) ${digitsOnly.substring(2, 7)}-${digitsOnly.substring(7, 11)}`;
  }

  const handleSetPixKeyValueFromFormValues = (form: any, newPixType: string) => {
    // console.log({ form });
    if (newPixType !== "BANK_ACCOUNT") {
      switch (newPixType) {
        case "DOCUMENT":
          form.setFieldValue("pixKey", proposal?.NaturalPerson?.document);
          break;

        case "PHONE":
          form.setFieldValue("pixKey", proposal?.NaturalPerson?.phoneNumber);
          break;

        case "EMAIL":
          form.setFieldValue(
            "pixKey",
            form.values.email || proposal?.NaturalPerson?.email
          );
          break;
        default:
          form.setFieldValue("pixKey", "");
          break;
      }
    }
  };

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, form: any, values: any) => {
      const inputValue = e.target.value;

      const { formatted, error } = validateAndFormatPhone(inputValue);
      const fieldName = values.pixKeyType === "PHONE" ? "pixKey" : "cellphone";
      form.setFieldValue(fieldName, formatted);

      if (error) {
        form.setFieldError("fieldName", error);
      } else {
        form.setFieldError("fieldName", undefined); // remove o erro
      }
    },
    []
  );

  const handleChangeBank = (e: any, form: any) => {
    form.setFieldValue("bankCode", e.target.value);
    const bankName = banks.filter((bank) => bank.id === e.target.value);
    form.setFieldValue("Bank", {
      name: bankName[0]?.name || "",
      id: bankName[0]?.id || "",
      ispb: bankName[0].ispb || "",
    });
  };

  const handleGoBackFormStep = () => {
    setCurrentStep((prev) => {
      if (prev === 1) {
        return prev;
      }
      return prev - 1;
    });
  };

  const getValidationSchema = (step: number) => {
    switch (step) {
      case 1:
        return personalDataValidationSchema;
      case 2:
        return addressValidationSchema;
      case 3:
        return bankDetailModalValidationSchema;
      default:
        return personalDataValidationSchema;
    }
  };

  const initialValues = {
    // Step 1 - Dados Pessoais
    name: proposal?.NaturalPerson?.name || "",
    document: proposal?.NaturalPerson?.document! || "",
    cellphone: formatarTelefone(proposal?.NaturalPerson?.phoneNumber || ""),
    email: proposal?.NaturalPerson?.email || "",
    motherName: proposal?.NaturalPerson?.motherName || "",
    birthDate: proposal?.NaturalPerson?.birthDate
      ? formatDateToBR(proposal?.NaturalPerson.birthDate)
      : "",

    // Step 2 - Endereço
    cep: cepFormatter(proposal?.NaturalPerson?.address?.zipCode) || "",
    street: proposal?.NaturalPerson?.address?.addressName || "",
    streetNumber: proposal?.NaturalPerson?.address?.number || "",
    complement: proposal?.NaturalPerson?.address?.complement || "",
    district: proposal?.NaturalPerson?.address?.district || "",
    city: proposal?.NaturalPerson?.address?.city || "",
    state: proposal?.NaturalPerson?.address?.uf || "",

    // Step 3 - Dados Bancários
    pixKeyType: proposal?.BankDetail?.pixKeyType || "",
    pixKey: proposal?.BankDetail?.pixKey || "",
    bankCode: proposal?.BankDetail?.bankCode || "",
    accountType: proposal?.BankDetail?.accountType || "",
    agency: proposal?.BankDetail?.agency || "",
    account: proposal?.BankDetail?.account || "",
    accountDigit: proposal?.BankDetail?.accountDigit || "",
    Bank: {
      id: proposal?.BankDetail?.Bank?.id || "",
      ispb: proposal?.BankDetail?.Bank?.ispb || "",
      name: proposal?.BankDetail?.Bank?.name || "",
    },
  };
  // const FormFooterNav = (props: any) => (
  //   <Flex justify="space-between" mt={6}>
  //     <Button size="sm" variant="ghost" onClick={onClose}>
  //       Cancelar
  //     </Button>
  //     <Flex gap={2}>
  //       <Button
  //         size="sm"
  //         colorScheme="gray"
  //         onClick={handleGoBackFormStep}
  //         isLoading={props.isSubmitting}
  //         leftIcon={<Icon as={MdNavigateBefore} />}
  //       >
  //         Voltar
  //       </Button>
  //       <Button
  //         size="sm"
  //         colorScheme="green"
  //         type={currentStep !== 3 ? 'button' : 'submit'}
  //         isLoading={props.isSubmitting}
  //         rightIcon={currentStep !== 3 ? <Icon as={MdNavigateNext} /> : undefined}
  //         onClick={() =>{
  //           switch (currentStep) {
  //             case 1:
  //               handlePersonalSubmit()
  //               break;

  //             default:
  //               break;
  //           }
  //         }}
  //       >
  //         {currentStep === 3 ? "Finalizar Atualização" : " Próximo"}
  //       </Button>
  //     </Flex>
  //   </Flex>
  // );

  const FormFooterNav = (props: any) => (
    <Flex justify="space-between" mt={6}>
      <Button size="sm" variant="ghost" onClick={onClose}>
        Cancelar
      </Button>
      <Flex gap={2}>
        {currentStep > 1 && (
          <Button
            size="sm"
            colorScheme="gray"
            onClick={handleGoBackFormStep}
            leftIcon={<Icon as={MdNavigateBefore} />}
          >
            Voltar
          </Button>
        )}
        {currentStep < 3 ? (
          <Button
            size="sm"
            colorScheme="green"
            type="button"
            rightIcon={<Icon as={MdNavigateNext} />}
            onClick={() => setCurrentStep(currentStep + 1)}
          >
            Próximo
          </Button>
        ) : (
          <Button
            size="sm"
            colorScheme="green"
            type="button" // Muda para button e chama submitForm manualmente
            isLoading={props.isSubmitting}
            onClick={() => {
              // console.log({ props });
              props.submitForm();
            }}
          >
            Finalizar Atualização
          </Button>
        )}
      </Flex>
    </Flex>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" justify="space-between" width="100%">
            <Flex align="center">
              <Icon
                as={
                  currentStep === 1
                    ? MdPerson
                    : currentStep === 2
                      ? MdHome
                      : MdAccountBalance
                }
                boxSize={8}
                color="gray.500"
                mr={2}
              />
              <Heading size="md" color="gray.700" fontFamily="Lexend Deca">
                {currentStep === 1
                  ? "Dados Pessoais"
                  : currentStep === 2
                    ? "Endereço"
                    : "Dados Bancários"}
              </Heading>
            </Flex>
            <ModalCloseButton position="static" />
          </Flex>
        </ModalHeader>

        <Divider mb={4} />

        <ModalBody>
          <StepIndicator currentStep={currentStep} />

          <Box>
            <Text fontSize="xs" fontWeight="nomal" color={"gray.800"} textAlign="center">
              {customStatus && formatErrorMessage(customStatus?.acoes?.correcao_dados_proposta_banco?.mensagem)}
            </Text>
          </Box>
          <Formik
            initialValues={initialValues}
            validationSchema={getValidationSchema(currentStep)}
            onSubmit={handleProposalRetry}
            enableReinitialize
          >
            {(props) => {
              return (
                <Form>
                  <VStack spacing={4} align="stretch">
                    {currentStep === 1 && (
                      <>
                        <Field name="name">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isInvalid={!!(form.errors.name && form.touched.name)}
                            >
                              <FormLabel htmlFor="name">Nome Completo</FormLabel>
                              <Input
                                {...field}
                                id="name"
                                placeholder="Digite o nome completo"
                                onChange={(e) =>
                                  form.setFieldValue("name", e.target.value.toUpperCase())
                                }
                              />
                              <FormErrorMessage>
                                {form.errors.name as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        <Field name="document">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isDisabled={true}
                              isInvalid={
                                !!(form.errors.document && form.touched.document)
                              }
                            >
                              <FormLabel htmlFor="document">CPF</FormLabel>
                              <Input
                                as={InputMask}
                                mask="999.999.999-99"
                                maskChar=""
                                {...field}
                                id="document"
                                placeholder="Digite o CPF"
                              />
                              <FormErrorMessage>
                                {form.errors.document as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                        <Field name="cellphone">
                          {({ field, form }: FieldProps) => {
                            return (
                              <FormControl
                                isInvalid={
                                  !!(form.errors.cellphone && form.touched.cellphone)
                                }
                              >
                                <FormLabel htmlFor="cellphone">Telefone</FormLabel>
                                <Input
                                  as={InputMask}
                                  mask="(99) 99999-9999"
                                  maskChar=""
                                  {...field}
                                  id="cellphone"
                                  placeholder="(xx) xxxxx-xxxx"
                                  type="tel"
                                  variant="flushed"
                                  onChange={(e) =>
                                    handlePhoneChange(e, form, props.values)
                                  }
                                  onBlur={(e) => {
                                    field.onBlur(e);
                                    form.setFieldTouched("cellphone", true, true);
                                    form.validateField("cellphone");
                                  }}
                                  w="100%"
                                />
                                <FormErrorMessage>
                                  {form.errors.cellphone as string}
                                </FormErrorMessage>
                              </FormControl>
                            );
                          }}
                        </Field>

                        <Field name="email">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isInvalid={!!(form.errors.email && form.touched.email)}
                            >
                              <FormLabel htmlFor="email">E-mail</FormLabel>
                              <Input
                                {...field}
                                id="email"
                                type="email"
                                placeholder="Digite o e-mail"
                                onChange={(e) => {
                                  let value = e.target.value;
                                  value = normalizeInput(value);
                                  form.setFieldValue("email", value);
                                }}
                              />
                              <FormErrorMessage>
                                {form.errors.email as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                        <Field name="motherName">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isInvalid={
                                !!(form.errors.motherName && form.touched.motherName)
                              }
                            >
                              <FormLabel htmlFor="motherName">Nome da Mãe</FormLabel>
                              <Input
                                {...field}
                                id="motherName"
                                placeholder="Digite o nome completo da mãe"
                                onChange={(e) =>
                                  form.setFieldValue(
                                    "motherName",
                                    e.target.value.toUpperCase()
                                  )
                                }
                              />
                              <FormErrorMessage>
                                {form.errors.motherName as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                        <Field name="birthDate">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isInvalid={
                                !!(form.errors.birthDate && form.touched.birthDate)
                              }
                            >
                              <FormLabel htmlFor="birthDate">
                                Data de Nascimento
                              </FormLabel>
                              <Input
                                {...field}
                                as={InputMask}
                                mask="99/99/9999"
                                maskChar=""
                                fontFamily="Lexend Deca"
                                id="birthDate"
                                placeholder="dd/mm/aaaa"
                                type="tel"
                                onChange={(e) => {
                                  handleBirthDateChange(e);
                                  form.setFieldValue("birthDate", e.target.value);
                                }}
                                value={birthDateProp || form.values.birthDate}
                                w="100%"
                              />
                              <FormErrorMessage>
                                {form.errors.birthDate as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </>
                    )}

                    {currentStep === 2 && (
                      <>
                        {/* CEP com botão de consulta */}
                        <Field name="cep">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isInvalid={!!(form.errors.cep && form.touched.cep)}
                            >
                              <FormLabel htmlFor="cep">CEP</FormLabel>
                              <HStack spacing={2}>
                                <Input
                                  {...field}
                                  placeholder="00000-000"
                                  maxLength={9}
                                  onChange={(e) =>
                                    form.setFieldValue(
                                      "cep",
                                      cepFormatter(e.target.value)
                                    )
                                  }
                                  flex={1}
                                />
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  leftIcon={<Icon as={MdSearch} />}
                                  isLoading={isCepLoading}
                                  onClick={() =>
                                    handleCepSearch(form.values.cep!, props.setFieldValue)
                                  }
                                >
                                  Consultar
                                </Button>
                              </HStack>
                              <FormErrorMessage>
                                {form.errors.cep as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        {/* Endereço */}
                        <Field name="street">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isDisabled={isInputReadOnly}
                              isInvalid={!!(form.errors.street && form.touched.street)}
                            >
                              <FormLabel>Endereço</FormLabel>
                              <Input
                                {...field}
                                placeholder="Digite o nome da rua"
                                onChange={(e) => {
                                  form.setFieldValue(
                                    "street",
                                    e.target.value.toUpperCase()
                                  );
                                }}
                              />
                              <FormErrorMessage>
                                {form.errors.street as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        {/* Número */}
                        <Field name="streetNumber">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isInvalid={
                                !!(form.errors.streetNumber && form.touched.streetNumber)
                              }
                            >
                              <FormLabel>Número</FormLabel>
                              <Input
                                {...field}
                                placeholder="Digite o número"
                                onChange={(e) => {
                                  const formattedNumber = e.target.value.replace(
                                    /\D/g,
                                    ""
                                  );
                                  form.setFieldValue("streetNumber", formattedNumber);
                                }}
                              />
                              <FormErrorMessage>
                                {form.errors.streetNumber as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        {/* Complemento */}
                        <Field name="complement">
                          {({ field, form }: FieldProps) => (
                            <FormControl>
                              <FormLabel>Complemento</FormLabel>
                              <Input
                                {...field}
                                placeholder="Digite o complemento (opcional)"
                                onChange={(e) => {
                                  form.setFieldValue(
                                    "complement",
                                    e.target.value.toUpperCase()
                                  );
                                }}
                              />
                              <FormErrorMessage>
                                {form.errors.complement as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        {/* Bairro */}
                        <Field name="district">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isDisabled={isInputReadOnly}
                              isInvalid={
                                !!(form.errors.district && form.touched.district)
                              }
                            >
                              <FormLabel>Bairro</FormLabel>
                              <Input
                                {...field}
                                placeholder="Digite o bairro"
                                onChange={(e) => {
                                  form.setFieldValue(
                                    "district",
                                    e.target.value.toUpperCase()
                                  );
                                }}
                              />
                              <FormErrorMessage>
                                {form.errors.district as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        {/* Estado */}
                        <Field name="state">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isDisabled={isInputReadOnly}
                              isInvalid={!!(form.errors.state && form.touched.state)}
                            >
                              <FormLabel>Estado</FormLabel>
                              <Select
                                {...field}
                                placeholder="Selecione o estado"
                                style={{
                                  pointerEvents: isInputReadOnly ? "none" : "auto",
                                }}
                              >
                                {statesArray.map((state) => (
                                  <option key={state} value={state}>
                                    {state}
                                  </option>
                                ))}
                              </Select>
                              <FormErrorMessage>
                                {form.errors.state as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        {/* Cidade */}
                        <Field name="city">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isDisabled={isInputReadOnly}
                              isInvalid={!!(form.errors.city && form.touched.city)}
                            >
                              <FormLabel>Cidade</FormLabel>
                              <Input
                                {...field}
                                placeholder="Digite a cidade"
                                onChange={(e) => {
                                  form.setFieldValue(
                                    "city",
                                    e.target.value.toUpperCase()
                                  );
                                }}
                              />
                              <FormErrorMessage>
                                {form.errors.city as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </>
                    )}

                    {currentStep === 3 && (
                      <>
                        <Field name="pixKeyType">
                          {({ field, form }: FieldProps) => (
                            <FormControl
                              isInvalid={
                                !!(form.errors.pixKeyType && form.touched.pixKeyType)
                              }
                            >
                              <FormLabel htmlFor="pixKeyType">
                                Chave Pix através
                              </FormLabel>
                              <Select
                                {...field}
                                id="pixKeyType"
                                onChange={(e) => {
                                  field.onChange(e);
                                  form.setFieldValue("pixKeyType", e.target.value);
                                  handleSetPixKeyValueFromFormValues(
                                    form,
                                    e.target.value
                                  );
                                  setIsPixKeyValid(true);
                                }}
                              >
                                {paymentMethods.map((method) => {
                                  const pixKeyType = mapPaymentMethodToPixKeyType(method);
                                  const label = mapPixKeyTypeToLabel(pixKeyType);
                                  return (
                                    <option key={pixKeyType} value={pixKeyType}>
                                      {label}
                                    </option>
                                  );
                                })}
                              </Select>
                              <FormErrorMessage>
                                {form.errors.pixKeyType as string}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        {props?.values?.pixKeyType === "BANK_ACCOUNT" ? (
                          <>
                            <Field name="bankCode">
                              {({ field, form }: FieldProps) => (
                                <FormControl
                                  isInvalid={
                                    !!(form.errors.bankCode && form.touched.bankCode)
                                  }
                                >
                                  <FormLabel htmlFor="bankCode">Banco</FormLabel>
                                  <Select
                                    {...field}
                                    id="bankCode"
                                    placeholder="Selecione o banco"
                                    isDisabled={isLoading}
                                    onChange={(e) => handleChangeBank(e, form)}
                                  >
                                    {banks.map((bank) => (
                                      <option key={bank.id} value={bank.id}>
                                        {bank.name}
                                      </option>
                                    ))}
                                  </Select>
                                  <FormErrorMessage>
                                    {form.errors.bankCode as string}
                                  </FormErrorMessage>
                                </FormControl>
                              )}
                            </Field>

                            <Field name="accountType">
                              {({ field, form }: FieldProps) => (
                                <FormControl
                                  isInvalid={
                                    !!(
                                      form.errors.accountType && form.touched.accountType
                                    )
                                  }
                                >
                                  <FormLabel htmlFor="accountType">
                                    Tipo de Conta
                                  </FormLabel>
                                  <Select
                                    {...field}
                                    id="accountType"
                                    placeholder="Selecione o tipo de conta"
                                  >
                                    {Object.entries(AccountTypeLabels).map(
                                      ([key, label]) => (
                                        <option key={key} value={key}>
                                          {label}
                                        </option>
                                      )
                                    )}
                                  </Select>
                                  <FormErrorMessage>
                                    {form.errors.accountType as string}
                                  </FormErrorMessage>
                                </FormControl>
                              )}
                            </Field>

                            <Field name="agency">
                              {({ field, form }: FieldProps) => (
                                <FormControl
                                  isInvalid={
                                    !!(form.errors.agency && form.touched.agency)
                                  }
                                >
                                  <FormLabel htmlFor="agency">Agência</FormLabel>
                                  <Input
                                    as={InputMask}
                                    mask="9999"
                                    maskChar=""
                                    {...field}
                                    id="agency"
                                    placeholder="Digite a agência"
                                  />
                                  <FormErrorMessage>
                                    {form.errors.agency as string}
                                  </FormErrorMessage>
                                </FormControl>
                              )}
                            </Field>

                            <Field name="account">
                              {({ field, form }: FieldProps) => (
                                <FormControl
                                  isInvalid={
                                    !!(form.errors.account && form.touched.account)
                                  }
                                >
                                  <FormLabel htmlFor="account">Número da Conta</FormLabel>
                                  <Input
                                    {...field}
                                    as={InputMask}
                                    mask="999999999"
                                    maskChar=""
                                    id="account"
                                    placeholder="Digite o número da conta"
                                  />
                                  <FormErrorMessage>
                                    {form.errors.account as string}
                                  </FormErrorMessage>
                                </FormControl>
                              )}
                            </Field>

                            <Field name="accountDigit">
                              {({ field, form }: FieldProps) => (
                                <FormControl
                                  isInvalid={
                                    !!(
                                      form.errors.accountDigit &&
                                      form.touched.accountDigit
                                    )
                                  }
                                >
                                  <FormLabel htmlFor="accountDigit">Dígito</FormLabel>
                                  <Input
                                    {...field}
                                    as={InputMask}
                                    mask="9"
                                    maskChar=""
                                    id="accountDigit"
                                    placeholder="Digite o dígito da conta"
                                  />
                                  <FormErrorMessage>
                                    {form.errors.accountDigit as string}
                                    {props.errors.accountDigit}
                                  </FormErrorMessage>
                                </FormControl>
                              )}
                            </Field>
                          </>
                        ) : props?.values?.pixKeyType ? (
                          <Field name="pixKey">
                            {({ field, form }: FieldProps) => {
                              let mask = "";
                              if (props?.values?.pixKeyType === "PHONE") {
                                mask = "(99) 99999-9999";
                              }
                              if (props?.values?.pixKeyType === "DOCUMENT") {
                                mask = "999.999.999-99";
                              }
                              return (
                                <FormControl
                                  isReadOnly={
                                    props?.values?.pixKeyType === "DOCUMENT" || props?.values?.pixKeyType === 'PHONE'
                                      ? true
                                      : false
                                  }
                                  isDisabled={
                                    props?.values?.pixKeyType === "DOCUMENT" || props?.values?.pixKeyType === 'PHONE'
                                      ? true
                                      : false
                                  }
                                  isInvalid={
                                    (!!form.errors.pixKey && !!form.touched.pixKey) ||
                                    !isPixKeyValid
                                  }
                                >
                                  <FormLabel htmlFor="pixKey">Chave Pix</FormLabel>
                                  <Input
                                    as={InputMask}
                                    mask={mask}
                                    maskChar=""
                                    {...field}
                                    id="pixKey"
                                    placeholder="Digite a chave pix"
                                    onChange={(e) => {
                                      let value = e.target.value;
                                      if (props?.values?.pixKeyType === "PHONE") {
                                        handlePhoneChange(e, form, props.values);
                                        return;
                                      }
                                      if (props?.values?.pixKeyType === "DOCUMENT") {
                                        value = cpfFormatter(value);
                                      }
                                      if (props?.values?.pixKeyType === "EMAIL") {
                                        value = normalizeInput(value);
                                        setIsPixKeyValid(!!emailChecker(value));
                                      }
                                      form.setFieldValue("pixKey", value);
                                    }}
                                  />
                                  <FormErrorMessage>
                                    {(!isPixKeyValid &&
                                      props?.values?.pixKeyType === "EMAIL" &&
                                      "Email inválido") ||
                                      (form.errors.pixKey as string)}
                                  </FormErrorMessage>
                                </FormControl>
                              );
                            }}
                          </Field>
                        ) : null}
                      </>
                    )}

                    <FormFooterNav {...props} />
                  </VStack>
                </Form>
              );
            }}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
